import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useBlocker, useNavigate, useOutletContext } from 'react-router-dom';
import api from '../../services/api';
import { tokens } from './marketingTokens';
import './ai-vis.css';

const FIXED_TZ = 'America/Los_Angeles';

const MAX_PROMPTS = 100;

function countPromptLines(raw) {
  if (!raw || !String(raw).trim()) return 0;
  return String(raw)
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((ln) => ln.trim())
    .filter(Boolean).length;
}

function linesToDomainList(text) {
  if (!text || !String(text).trim()) return [];
  return String(text)
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

const ALL_SERVICES = ['chatgpt', 'google_ai_overviews'];

/** Run frequency options — value matches backend `RunFrequency`. */
const FREQUENCY_OPTIONS = [
  {
    value: 'quarter_yearly',
    label: 'Quarterly',
    hint: '~4× per year',
    Icon: IconQuarterly,
  },
  { value: 'monthly', label: 'Monthly', hint: 'Once a month', Icon: IconMonthly },
  { value: 'bi_weekly', label: 'Bi-weekly', hint: 'Twice a month', Icon: IconBiWeekly },
  { value: 'weekly', label: 'Weekly', hint: 'Every week', Icon: IconWeekly },
  { value: 'three_days', label: 'Every 3 days', hint: 'Frequent checks', Icon: IconThreeDays },
  { value: 'daily', label: 'Daily', hint: 'Highest cadence', Icon: IconDaily },
];

function FrequencyIconWrap({ children }) {
  return <span className="aivis-frequency-card-icon-wrap">{children}</span>;
}

const strokeSvg = {
  className: 'aivis-frequency-card-svg',
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.65,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
};

function IconQuarterly() {
  return (
    <FrequencyIconWrap>
      <svg {...strokeSvg}>
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <path d="M3 10h18M8 2v4M16 2v4" />
        <path d="M7 14h.01M12 14h.01M17 14h.01M7 18h.01M12 18h.01" />
      </svg>
    </FrequencyIconWrap>
  );
}

function IconMonthly() {
  return (
    <FrequencyIconWrap>
      <svg {...strokeSvg}>
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <path d="M3 10h18M8 2v4M16 2v4" />
        <path d="M12 14v4M10 16h4" />
      </svg>
    </FrequencyIconWrap>
  );
}

function IconBiWeekly() {
  return (
    <FrequencyIconWrap>
      <svg {...strokeSvg}>
        <path d="M4 12h4l2-3 4 6 2-3h4" />
        <path d="M4 18h16" />
      </svg>
    </FrequencyIconWrap>
  );
}

function IconWeekly() {
  return (
    <FrequencyIconWrap>
      <svg {...strokeSvg}>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </svg>
    </FrequencyIconWrap>
  );
}

function IconThreeDays() {
  return (
    <FrequencyIconWrap>
      <svg {...strokeSvg}>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 1.5" />
        <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
      </svg>
    </FrequencyIconWrap>
  );
}

function IconDaily() {
  return (
    <FrequencyIconWrap>
      <svg {...strokeSvg}>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
      </svg>
    </FrequencyIconWrap>
  );
}

function SearchableSelect({ id, value, onChange, options, placeholder = 'Search…' }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return options;
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [query, options]);

  useEffect(() => {
    if (!open) return;
    function handler(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  function handleOpen() {
    setOpen(true);
    setQuery('');
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  function handleSelect(tz) {
    onChange(tz);
    setOpen(false);
    setQuery('');
  }

  function handleKeyDown(e) {
    if (e.key === 'Escape') { setOpen(false); setQuery(''); }
    if (e.key === 'Enter' && filtered.length > 0) handleSelect(filtered[0]);
    if (e.key === 'ArrowDown' && listRef.current) {
      const first = listRef.current.querySelector('[data-tz-option]');
      first?.focus();
    }
  }

  function handleOptionKeyDown(e, tz) {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSelect(tz); }
    if (e.key === 'Escape') { setOpen(false); setQuery(''); }
    if (e.key === 'ArrowDown') { e.preventDefault(); e.currentTarget.nextElementSibling?.focus(); }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = e.currentTarget.previousElementSibling;
      if (prev) prev.focus(); else inputRef.current?.focus();
    }
  }

  return (
    <div ref={containerRef} className="aivis-searchable-select" id={id}>
      <button
        type="button"
        className="aivis-searchable-select-trigger"
        onClick={handleOpen}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="aivis-searchable-select-value">{value || placeholder}</span>
        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="aivis-searchable-select-dropdown" role="listbox" aria-label="Select timezone">
          <div className="aivis-searchable-select-search-wrap">
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              ref={inputRef}
              className="aivis-searchable-select-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search timezones…"
              aria-label="Search timezones"
              autoComplete="off"
            />
            {query && (
              <button type="button" className="aivis-searchable-select-clear" onClick={() => setQuery('')} aria-label="Clear search">
                ×
              </button>
            )}
          </div>
          <div ref={listRef} className="aivis-searchable-select-list">
            {filtered.length === 0 ? (
              <div className="aivis-searchable-select-empty">No timezones match "{query}"</div>
            ) : (
              filtered.map((tz) => (
                <div
                  key={tz}
                  data-tz-option
                  role="option"
                  aria-selected={tz === value}
                  tabIndex={0}
                  className={`aivis-searchable-select-option${tz === value ? ' aivis-searchable-select-option--selected' : ''}`}
                  onClick={() => handleSelect(tz)}
                  onKeyDown={(e) => handleOptionKeyDown(e, tz)}
                >
                  {tz}
                  {tz === value && (
                    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ClientDropdown({ clients, value, onChange, loading }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [closing, setClosing] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const sorted = useMemo(() => {
    const list = [...clients].sort((a, b) => a.name.localeCompare(b.name));
    const q = query.toLowerCase().trim();
    return q ? list.filter((c) => c.name.toLowerCase().includes(q)) : list;
  }, [clients, query]);

  useEffect(() => {
    if (!open) return;
    function handler(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) doClose();
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  function doOpen() {
    setClosing(false);
    setMounted(true);
    setOpen(true);
    setQuery('');
    setTimeout(() => inputRef.current?.focus(), 20);
  }

  function doClose() {
    setClosing(true);
    setOpen(false);
  }

  function handleAnimEnd() {
    if (closing) { setMounted(false); setClosing(false); }
  }

  function handleSelect(client) {
    onChange(client);
    doClose();
    setQuery('');
  }

  const selectedClient = clients.find((c) => c.name === value);

  return (
    <div ref={containerRef} className="aivis-client-dd-wrap">
      <button
        type="button"
        className={`aivis-client-dd-trigger${value ? ' aivis-client-dd-trigger--selected' : ''}`}
        onClick={() => (open ? doClose() : doOpen())}
        disabled={loading}
      >
        <span className="aivis-client-dd-trigger-value">
          {loading ? 'Loading clients…' : (selectedClient?.name || 'Select a client…')}
        </span>
        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <polyline points={open ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
        </svg>
      </button>

      {mounted && (
        <div
          className={`aivis-client-dd-panel${closing ? ' aivis-client-dd-panel--closing' : ''}`}
          onAnimationEnd={handleAnimEnd}
          role="listbox"
        >
          <div className="aivis-client-dd-search-wrap">
            <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              ref={inputRef}
              className="aivis-client-dd-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search clients…"
              autoComplete="off"
            />
            {query && <button type="button" className="aivis-client-dd-clear" onClick={() => setQuery('')}>×</button>}
          </div>
          <div className="aivis-client-dd-list">
            {sorted.length === 0 ? (
              <div className="aivis-client-dd-empty">No clients match.</div>
            ) : sorted.map((c) => (
              <div
                key={c.id ?? c.name}
                role="option"
                aria-selected={c.name === value}
                className={`aivis-client-dd-option${c.name === value ? ' aivis-client-dd-option--selected' : ''}`}
                onClick={() => handleSelect(c)}
                tabIndex={0}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleSelect(c)}
              >
                <span>{c.name}</span>
                {c.name === value && (
                  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function CampaignWizardPage() {
  const navigate = useNavigate();
  const { refreshCampaigns, selectedOrgId, orgs } = useOutletContext();

  const [step, setStep] = useState(0);
  const [clientName, setClientName] = useState('');
  const [clientWebsite, setClientWebsite] = useState('');
  const [alternativeNames, setAlternativeNames] = useState('');
  const [clientOverview, setClientOverview] = useState('');
  const [coreOfferings, setCoreOfferings] = useState('');
  const [doNotContactDomainsText, setDoNotContactDomainsText] = useState('');
  const [promptsText, setPromptsText] = useState('');
  const [frequency, setFrequency] = useState('weekly');
  const [runsPerPeriod, setRunsPerPeriod] = useState(5);
  const [preferredWeekday, setPreferredWeekday] = useState(5); // Saturday
  const [runsOnPreferredDay, setRunsOnPreferredDay] = useState(5);
  const [runGapHours, setRunGapHours] = useState(1);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [advancedMounted, setAdvancedMounted] = useState(false);
  const [advancedClosing, setAdvancedClosing] = useState(false);
  const [reviewIconUrl, setReviewIconUrl] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // Use a ref (not state) so the blocker function sees it synchronously without a re-render race
  const submittedRef = useRef(false);

  // Identify the currently selected organization
  const selectedOrg = useMemo(
    () => (orgs || []).find((o) => o.id === selectedOrgId) || null,
    [orgs, selectedOrgId],
  );
  // NocoDB is only available for Visibility Labs — 180 Marketing uses manual entry
  const isVisibilityLabs = useMemo(
    () => Boolean(selectedOrg?.name?.toLowerCase().includes('visibility')),
    [selectedOrg],
  );

  // NocoDB client list
  const [nocoClients, setNocoClients] = useState([]);
  const [nocoLoading, setNocoLoading] = useState(false);
  const [nocoClientLoading, setNocoClientLoading] = useState(false);

  useEffect(() => {
    if (!isVisibilityLabs) {
      setNocoClients([]);
      return;
    }
    setNocoLoading(true);
    api.get('/aivis/nocodb/clients/')
      .then(({ data }) => setNocoClients(data.clients || []))
      .catch(() => setNocoClients([]))
      .finally(() => setNocoLoading(false));
  }, [isVisibilityLabs]);

  async function handleClientSelect(client) {
    setClientName(client.name);
    if (client.client_overview) setClientOverview(client.client_overview);
    if (client.core_offerings) setCoreOfferings(client.core_offerings);
    if (client.branded_names) setAlternativeNames(client.branded_names);
    if (client.website) setClientWebsite(client.website);

    setNocoClientLoading(true);
    try {
      const [dncRes, promptsRes] = await Promise.all([
        api.get(`/aivis/nocodb/clients/${encodeURIComponent(client.name)}/dnc/`),
        api.get(`/aivis/nocodb/clients/${encodeURIComponent(client.name)}/prompts/`),
      ]);
      if (dncRes.data?.domains?.length) {
        setDoNotContactDomainsText(dncRes.data.domains.join('\n'));
      }
      if (promptsRes.data?.prompts?.length) {
        setPromptsText(promptsRes.data.prompts.join('\n'));
      }
    } catch {
      // non-fatal — user can fill manually
    } finally {
      setNocoClientLoading(false);
    }
  }

  const isDirty = step > 0 || !!clientName || !!clientWebsite || !!promptsText || !!clientOverview;

  // Block in-app navigation when the user has made progress
  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    isDirty && !submittedRef.current && currentLocation.pathname !== nextLocation.pathname
  );

  // Block browser tab close / refresh
  useEffect(() => {
    const handler = (e) => {
      if (!isDirty || submittedRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Steps: Client(0) → Prompts(1) → Review(2)
  const steps = useMemo(() => ['Client', 'Prompts', 'Review'], []);

  const promptLineCount = useMemo(() => countPromptLines(promptsText), [promptsText]);

  useEffect(() => {
    if (step !== 2) { // Review is now step 2
      setReviewIconUrl('');
      return undefined;
    }
    let website = clientWebsite.trim();
    if (website && !/^https?:\/\//i.test(website)) {
      website = `https://${website}`;
    }
    if (!website) {
      setReviewIconUrl('');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/aivis/campaigns/preview-brand-icon/', {
          params: { url: website },
        });
        if (!cancelled) setReviewIconUrl(data?.icon_url || '');
      } catch {
        if (!cancelled) setReviewIconUrl('');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step, clientWebsite]);

  async function submit() {
    setError('');
    if (isVisibilityLabs) {
      if (!clientOverview.trim()) {
        setError('Client overview is required.');
        return;
      }
      if (!coreOfferings.trim()) {
        setError('Core offering(s) is required.');
        return;
      }
    }
    let website = clientWebsite.trim();
    if (website && !/^https?:\/\//i.test(website)) {
      website = `https://${website}`;
    }

    setBusy(true);
    try {
      const { data } = await api.post('/aivis/campaigns/', {
        name: clientName.trim(),
        client_name: clientName.trim(),
        client_website: website,
        client_timezone: FIXED_TZ,
        first_run_at: new Date().toISOString(),
        alternative_names: alternativeNames,
        client_overview: isVisibilityLabs ? clientOverview : '',
        core_offerings: isVisibilityLabs ? coreOfferings : '',
        do_not_contact_domains: isVisibilityLabs ? linesToDomainList(doNotContactDomainsText) : [],
        brand_color_hex: '',
        prompts_text: promptsText,
        services: ALL_SERVICES,
        frequency,
        runs_per_period: Number(runsPerPeriod),
        preferred_weekday: Number(preferredWeekday),
        runs_on_preferred_day: Number(runsOnPreferredDay),
        run_gap_hours: Number(runGapHours),
        ...(selectedOrgId ? { organization_id: selectedOrgId } : {}),
      });
      await refreshCampaigns();
      submittedRef.current = true; // set ref BEFORE navigate so blocker sees it immediately
      // Navigate with the campaign ID in the URL so AiVisLayout picks it up without a race condition
      navigate(data?.id ? `/ai-vis/home?campaign=${data.id}` : '/ai-vis/home');
    } catch (e) {
      const d = e.response?.data;
      const msg =
        (typeof d === 'string' && d) ||
        d?.detail ||
        (d && JSON.stringify(d)) ||
        'Could not save campaign.';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {/* ── Abandon warning modal ── */}
      {blocker.state === 'blocked' && (
        <div className="aivis-warn-overlay" role="dialog" aria-modal="true" aria-labelledby="aivis-warn-title">
          <div className="aivis-warn-modal">
            <div className="aivis-warn-modal-icon" aria-hidden="true">
              <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke={tokens.caution500} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <h2 className="aivis-warn-modal-title" id="aivis-warn-title">Leave without saving?</h2>
            <p className="aivis-warn-modal-body">
              You&apos;ve started setting up a new campaign. If you leave now, all your progress will be lost and cannot be recovered.
            </p>
            <div className="aivis-warn-modal-actions">
              <button className="aivis-warn-modal-keep" onClick={() => blocker.reset()}>
                Keep editing
              </button>
              <button className="aivis-warn-modal-leave" onClick={() => blocker.proceed()}>
                Leave anyway
              </button>
            </div>
          </div>
        </div>
      )}

      <h1 className="aivis-page-title">New client</h1>
      <p className="aivis-page-sub">Configure client prompts and tracking; Celery workers handle execution at scale.</p>

      {/* ── Milestone stepper ── */}
      <div className="aivis-wizard-stepper" role="list" aria-label="Setup progress">
        {steps.map((label, i) => {
          const state = i < step ? 'done' : i === step ? 'active' : 'pending';
          return (
            <div
              key={label}
              className={`aivis-wizard-milestone aivis-wizard-milestone--${state}`}
              role="listitem"
              aria-current={state === 'active' ? 'step' : undefined}
            >
              <div className="aivis-wizard-milestone-node">
                {state === 'done' ? (
                  <svg className="aivis-wizard-milestone-check" width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  <span>{i + 1}</span>
                )}
              </div>
              <span className="aivis-wizard-milestone-label">{label}</span>
            </div>
          );
        })}
      </div>


      <div className="aivis-card">
        {error && <div className="aivis-auth-error">{error}</div>}

        {step === 0 && (
          <>
            {/* Organization badge */}
            {selectedOrg && (
              <div className="aivis-wizard-org-badge">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M3 21V7l9-4 9 4v14"/>
                  <path d="M9 21v-6h6v6"/>
                  <path d="M9 9h.01M15 9h.01M9 13h.01M15 13h.01"/>
                </svg>
                This client will be added to <strong>{selectedOrg.name}</strong>
              </div>
            )}

            <div className="aivis-field">
              <label>Client name</label>
              {isVisibilityLabs ? (
                <>
                  <ClientDropdown
                    clients={nocoClients}
                    value={clientName}
                    onChange={(c) => handleClientSelect(c)}
                    loading={nocoLoading}
                  />
                  {nocoClientLoading && (
                    <p className="aivis-service-hint" style={{ marginTop: 8 }}>
                      Loading client data from NocoDB…
                    </p>
                  )}
                </>
              ) : (
                <input
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  required
                  placeholder="Client name"
                />
              )}
            </div>

            <div className="aivis-field">
              <label>Client website</label>
              <input
                value={clientWebsite}
                onChange={(e) => setClientWebsite(e.target.value)}
                placeholder="https://example.com"
                required
              />
            </div>

            {isVisibilityLabs && (
              <>
                <div className="aivis-field">
                  <label htmlFor="aivis-client-overview">Client overview (required)</label>
                  <p className="aivis-service-hint" style={{ marginTop: 0 }}>
                    Who the client is, market, and positioning. Used when scoring source pages for PR fit (Gemini).
                  </p>
                  <textarea
                    id="aivis-client-overview"
                    value={clientOverview}
                    onChange={(e) => setClientOverview(e.target.value)}
                    placeholder="e.g. B2B SaaS for field service teams; mid-market; US + EU."
                    rows={5}
                    required
                  />
                </div>
                <div className="aivis-field">
                  <label htmlFor="aivis-core-offerings">Core offering(s) (required)</label>
                  <p className="aivis-service-hint" style={{ marginTop: 0 }}>
                    Products or services to judge whether a roundup is a good pitching fit.
                  </p>
                  <textarea
                    id="aivis-core-offerings"
                    value={coreOfferings}
                    onChange={(e) => setCoreOfferings(e.target.value)}
                    placeholder="e.g. Scheduling software, mobile crew apps, integrations with QuickBooks."
                    rows={4}
                    required
                  />
                </div>
                <div className="aivis-field">
                  <label>Do not contact domains (optional)</label>
                  <p className="aivis-service-hint" style={{ marginTop: 0 }}>
                    Domains we will not send to prospecting outreach. They still appear in Cited Domains and Cited URLs. Enter the bare domain only — no <code>https://</code> or trailing slashes (e.g. <code>example.com</code>, not <code>https://example.com/</code>). You can add multiple domains separated by commas or one per line.
                  </p>
                  <textarea
                    value={doNotContactDomainsText}
                    onChange={(e) => setDoNotContactDomainsText(e.target.value)}
                    placeholder={'competitor.com\nanotherdomain.com'}
                    rows={3}
                  />
                </div>
              </>
            )}

            <div className="aivis-field">
              <label>Alternative / industry names (required)</label>
              <textarea
                value={alternativeNames}
                onChange={(e) => setAlternativeNames(e.target.value)}
                placeholder="One per line or comma-separated"
                required
              />
            </div>

            {/* ── Advanced scheduling ── */}
            <div className="aivis-run-plan-summary" style={{ marginTop: 24, marginBottom: 12 }}>
              <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>
                Every <strong>{FREQUENCY_OPTIONS.find(f => f.value === frequency)?.label || 'week'}</strong>, this campaign runs <strong>{runsOnPreferredDay} time{Number(runsOnPreferredDay) !== 1 ? 's' : ''}</strong> on <strong>{['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'][Number(preferredWeekday)]}</strong> with <strong>{runGapHours} hour{Number(runGapHours) !== 1 ? 's' : ''}</strong> between each run.
              </span>
            </div>

            <button
              type="button"
              className="aivis-advanced-toggle"
              onClick={() => {
                if (advancedOpen) {
                  setAdvancedOpen(false);
                  setAdvancedClosing(true);
                } else {
                  setAdvancedClosing(false);
                  setAdvancedMounted(true);
                  setAdvancedOpen(true);
                }
              }}
              aria-expanded={advancedOpen}
            >
              <svg
                width={14} height={14} viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"
                aria-hidden
                style={{ transform: advancedOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
              Advanced scheduling options
            </button>

            {advancedMounted && (
              <div
                className={`aivis-advanced-panel${advancedClosing ? ' aivis-advanced-panel--closing' : ''}`}
                onAnimationEnd={() => {
                  if (advancedClosing) { setAdvancedMounted(false); setAdvancedClosing(false); }
                }}
              >
                <label id="aivis-frequency-label" style={{ fontWeight: 600, fontSize: 14, marginBottom: 4, display: 'block' }}>Run frequency</label>
                <p className="aivis-frequency-hint" style={{ marginTop: 0 }}>How often we queue visibility runs for this campaign.</p>
                <div
                  className="aivis-frequency-grid"
                  role="radiogroup"
                  aria-labelledby="aivis-frequency-label"
                >
                  {FREQUENCY_OPTIONS.map(({ value, label, hint, Icon: FreqIcon }) => {
                    const selected = frequency === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        className={`aivis-frequency-card ${selected ? 'aivis-frequency-card-selected' : ''}`}
                        onClick={() => setFrequency(value)}
                      >
                        <FreqIcon />
                        <span className="aivis-frequency-card-label">{label}</span>
                        <span className="aivis-frequency-card-hint">{hint}</span>
                      </button>
                    );
                  })}
                </div>

                <div className="aivis-run-plan-grid" style={{ marginTop: 20 }}>
                  <div className="aivis-run-plan-field">
                    <label className="aivis-run-plan-field-label">Preferred day of the week</label>
                    <p className="aivis-frequency-hint" style={{ marginTop: 0, marginBottom: 6 }}>
                      Runs will be concentrated on this day when possible.
                    </p>
                    <select
                      className="aivis-select"
                      style={{ width: '100%' }}
                      value={preferredWeekday}
                      onChange={(e) => setPreferredWeekday(e.target.value)}
                    >
                      <option value={0}>Monday</option>
                      <option value={1}>Tuesday</option>
                      <option value={2}>Wednesday</option>
                      <option value={3}>Thursday</option>
                      <option value={4}>Friday</option>
                      <option value={5}>Saturday</option>
                      <option value={6}>Sunday</option>
                    </select>
                  </div>
                  <div className="aivis-run-plan-field">
                    <label className="aivis-run-plan-field-label">Runs on preferred day</label>
                    <p className="aivis-frequency-hint" style={{ marginTop: 0, marginBottom: 6 }}>
                      How many runs happen on the preferred day each period.
                    </p>
                    <input
                      type="number"
                      min={1}
                      max={30}
                      className="aivis-input-time"
                      style={{ width: '100%' }}
                      value={runsOnPreferredDay}
                      onChange={(e) => { setRunsOnPreferredDay(e.target.value); setRunsPerPeriod(e.target.value); }}
                    />
                  </div>
                  <div className="aivis-run-plan-field">
                    <label className="aivis-run-plan-field-label">Gap between runs (hours)</label>
                    <p className="aivis-frequency-hint" style={{ marginTop: 0, marginBottom: 6 }}>
                      Minimum hours to wait between consecutive runs on the same day.
                    </p>
                    <input
                      type="number"
                      min={1}
                      max={24}
                      className="aivis-input-time"
                      style={{ width: '100%' }}
                      value={runGapHours}
                      onChange={(e) => setRunGapHours(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {step === 1 && (
          <div className="aivis-field">
            <div className="aivis-field-label-row">
              <label htmlFor="aivis-wizard-prompts">Prompts (one per line, up to {MAX_PROMPTS})</label>
              <span
                className={[
                  'aivis-prompt-count-capsule',
                  promptLineCount === MAX_PROMPTS ? 'aivis-prompt-count-capsule-limit' : '',
                  promptLineCount > MAX_PROMPTS ? 'aivis-prompt-count-capsule-over' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                aria-live="polite"
              >
                {promptLineCount}/{MAX_PROMPTS}
              </span>
            </div>
            <textarea
              id="aivis-wizard-prompts"
              className="aivis-wizard-prompts-textarea"
              value={promptsText}
              onChange={(e) => setPromptsText(e.target.value)}
              placeholder={'Best CRM for small teams?\nHow does ExampleCo compare to…'}
              rows={30}
              required
            />
          </div>
        )}

        {step === 2 && (
          <div className="aivis-review-block">
            {reviewIconUrl ? (
              <img
                src={reviewIconUrl}
                alt=""
                className="aivis-review-brand-logo"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : null}
            <div className="aivis-review-summary aivis-page-sub" style={{ fontSize: 14, lineHeight: 1.6 }}>
              <p>
                <strong style={{ color: tokens.graphite800 }}>Client:</strong> {clientName || '—'} ({clientWebsite || '—'})
              </p>
              <p style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                <strong style={{ color: tokens.graphite800 }}>Tracking:</strong>
                {[
                  { id: 'chatgpt', label: 'ChatGPT', icon: '/chatgpt-icon.png' },
                  { id: 'google_ai_overviews', label: 'Google AI Overviews', icon: '/google-icon.png' },
                ].map((s) => (
                  <span key={s.id} className="aivis-review-service-pill">
                    <img src={s.icon} alt="" width={18} height={18} className="aivis-review-service-pill-img" />
                    {s.label}
                  </span>
                ))}
              </p>
              <p>
                <strong style={{ color: tokens.graphite800 }}>Schedule:</strong>{' '}
                Every {FREQUENCY_OPTIONS.find((f) => f.value === frequency)?.label?.toLowerCase() || 'week'} — {runsOnPreferredDay} run{Number(runsOnPreferredDay) !== 1 ? 's' : ''} on {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'][Number(preferredWeekday)]}, {runGapHours}h apart
              </p>
              <p>
                <strong style={{ color: tokens.graphite800 }}>Prompts:</strong>{' '}
                {promptLineCount}/{MAX_PROMPTS}
              </p>
            </div>
          </div>
        )}

        <div className="aivis-wizard-actions">
           {step > 0 ? (
          <button type="button" className="aivis-btn-secondary" onClick={() => setStep((s) => s - 1)}>
            Back
          </button>
        ) : (
          <span />
        )}
          {step < steps.length - 1 ? (
            <button
              type="button"
              className="aivis-btn-primary"
              style={{ width: 'auto' }}
              disabled={
                (step === 0 && (!clientName.trim() || !clientWebsite.trim() || !alternativeNames.trim()))
                || (step === 1 && (promptLineCount < 1 || promptLineCount > MAX_PROMPTS))
              }
              onClick={() => setStep((s) => s + 1)}
            >
              Continue
            </button>
          ) : (
            <button type="button" className="aivis-btn-primary" style={{ width: 'auto' }} disabled={busy || submittedRef.current} onClick={submit}>
              {busy ? 'Saving…' : 'Save & queue run'}
            </button>
          )}
        </div>
      </div>
    </>
  );
}
